import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  MeetingResponse,
  MeetingTypeResponse,
  MeetingStatusResponse,
  MeetingParticipantResponse,
  MeetingAgendaItemResponse,
  MeetingAgendaItemRequest,
  MeetingReminderResponse,
  MeetingReminderRequest,
  MeetingRescheduleLogResponse,
  MeetingAuditLogResponse,
  MeetingEmailTemplateResponse,
  MeetingBookingFormFieldResponse,
  ReminderMethod,
  ReminderTarget,
  ReminderStatus,
  MEETING_PAYMENT_STATUS_LABELS,
  MEETING_SOURCE_LABELS,
  PARTICIPANT_STATUS_LABELS,
  VIDEO_PROVIDER_LABELS,
  meetingLabel,
} from '../models/meeting.model';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { JitsiRoomComponent } from '../jitsi-room/jitsi-room.component';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { MeetingApiService } from '../services/meeting-api.service';
import { MeetingTypeApiService } from '../services/meeting-type-api.service';
import { MeetingStatusApiService } from '../services/meeting-status-api.service';
import { MeetingEmailTemplateApiService } from '../services/meeting-email-template-api.service';
import { MeetingBooksLinkApiService } from '../services/meeting-books-link-api.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { DealApiService } from '../../deals/services/deal-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { catchError, forkJoin, of } from 'rxjs';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { isMeetingCancelled } from '../shared/meeting-action.util';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';
type TabKey = 'overview' | 'participants' | 'agenda' | 'reminders' | 'logs';

interface AgendaFormModel {
  title: string;
  description: string;
  durationMinutes: number | null;
  presenter: string;
  order: number | null;
}

interface ReminderFormModel {
  reminderMinutesBefore: number | null;
  reminderMethod: ReminderMethod;
  reminderTarget: ReminderTarget;
  /** INC 72 — plantilla de correo (solo email/all). null = plantilla por defecto. */
  templateId: number | null;
}

@Component({
  selector: 'app-meeting-detail-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent, BadgeComponent, JitsiRoomComponent, AuditTimelineComponent, HasPermissionDirective],
  templateUrl: './meeting-detail-modal.component.html',
})
export class MeetingDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() meeting: MeetingResponse | null = null;
  @Output() close = new EventEmitter<void>();

  activeTab: TabKey = 'overview';

  // Videollamada Jitsi inline (organizador entra sin salir del CRM)
  jitsiOpen = false;

  // Relaciones
  contactName = '';
  companyName = '';
  dealName = '';
  organizerName = '';

  /**
   * Respuestas a los campos adicionales de la página de reserva (Editar página → Campos
   * del formulario). La reserva pública las guarda en `preQualificationData`, pero ninguna
   * pantalla las mostraba: lo que el invitado contestaba no se veía por ningún lado.
   */
  bookingAnswers: { label: string; value: string }[] = [];

  // Catálogos
  meetingTypes: MeetingTypeResponse[] = [];
  meetingStatuses: MeetingStatusResponse[] = [];

  // Sub-recursos (lazy)
  participants: MeetingParticipantResponse[] = [];
  agendaItems: MeetingAgendaItemResponse[] = [];
  reminders: MeetingReminderResponse[] = [];
  rescheduleLogs: MeetingRescheduleLogResponse[] = [];
  auditLogs: MeetingAuditLogResponse[] = [];

  // INC 47 — logs mapeados al shape estándar que consume <app-audit-timeline>
  timelineLogs: AuditLogResponse[] = [];

  loadingParticipants = false;
  loadingAgenda = false;
  loadingReminders = false;
  loadingLogs = false;

  private loadedParticipants = false;
  private loadedAgenda = false;
  private loadedReminders = false;
  private loadedLogs = false;

  // INC 45 — formulario inline de Agenda
  showAgendaForm = false;
  editingAgendaId: number | null = null;
  savingAgenda = false;
  agendaModel: AgendaFormModel = this.emptyAgendaModel();

  // INC 46 — formulario inline de Recordatorios
  showReminderForm = false;
  editingReminderId: number | null = null;
  savingReminder = false;
  reminderModel: ReminderFormModel = this.emptyReminderModel();

  // INC 72 — plantillas de correo (templateType=reminder) para el selector del recordatorio.
  reminderTemplates: MeetingEmailTemplateResponse[] = [];
  /** true si la carga de plantillas FALLÓ (distinto de "no hay ninguna"). */
  reminderTemplatesError = false;
  private loadedReminderTemplates = false;

  readonly reminderMinutePresets: { value: number; label: string }[] = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 60, label: '1 hora' },
    { value: 1440, label: '1 día' },
  ];
  readonly reminderMethodOptions: { value: ReminderMethod; label: string }[] = [
    { value: 'email', label: 'Correo' },
    { value: 'inapp', label: 'Notificación in-app' },
    { value: 'all', label: 'Ambos' },
  ];
  readonly reminderTargetOptions: { value: ReminderTarget; label: string }[] = [
    { value: 'organizer', label: 'Organizador' },
    { value: 'guests', label: 'Invitados' },
    { value: 'all', label: 'Todos' },
  ];

  constructor(
    private meetingApi: MeetingApiService,
    private meetingTypeApi: MeetingTypeApiService,
    private meetingStatusApi: MeetingStatusApiService,
    private emailTemplateApi: MeetingEmailTemplateApiService,
    private booksLinkApi: MeetingBooksLinkApiService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['meeting'] && this.meeting) {
      this.resetSubResources();
      this.activeTab = 'overview';
      this.cargarCatalogos();
      this.cargarNombresRelaciones();
      this.cargarOrganizador();
      this.cargarRespuestasReserva();
    }
  }

  /**
   * Arma las respuestas del formulario de reserva con la etiqueta de cada campo tal como la
   * vio el invitado. Se piden TODOS los campos de la página (no solo los activos) para que
   * un campo desactivado después de la reserva conserve su etiqueta.
   */
  private cargarRespuestasReserva(): void {
    this.bookingAnswers = [];
    const answers = this.parsePreQualification(this.meeting?.preQualificationData);
    if (!answers) return;

    const meetingId = this.meeting?.id;
    const bookingLinkId = this.meeting?.idBookingLink;
    const fields$ = bookingLinkId
      ? this.booksLinkApi.listFormFields(bookingLinkId)
          .pipe(catchError(() => of([] as MeetingBookingFormFieldResponse[])))
      : of([] as MeetingBookingFormFieldResponse[]);

    fields$.subscribe((fields) => {
      if (this.meeting?.id !== meetingId) return; // se abrió otra reunión mientras tanto
      const byName = new Map(fields.map((f) => [f.fieldName, f]));
      const known = [...fields]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .filter((f) => f.fieldName in answers);
      const unknown = Object.keys(answers).filter((k) => !byName.has(k));

      this.bookingAnswers = [
        ...known.map((f) => ({
          label: f.fieldLabel?.trim() || this.humanizeKey(f.fieldName),
          value: this.formatAnswer(answers[f.fieldName], f.fieldType),
        })),
        ...unknown.map((k) => ({ label: this.humanizeKey(k), value: this.formatAnswer(answers[k]) })),
      ].filter((a) => a.value !== '');
    });
  }

  /**
   * `preQualificationData` solo es un objeto de respuestas cuando lo escribió la reserva.
   * Si la página no tenía campos, el backend guarda ahí la agenda por defecto (un arreglo):
   * eso no son respuestas y no se muestra.
   */
  private parsePreQualification(raw?: string | null): Record<string, unknown> | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      return Object.fromEntries(Object.entries(parsed).filter(([k]) => !k.startsWith('_')));
    } catch {
      return null;
    }
  }

  private formatAnswer(value: unknown, fieldType?: string): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean).join(', ');
    const text = String(value).trim();
    // Fecha de un <input type="date"> (YYYY-MM-DD): se reordena sin pasar por Date,
    // que la leería como UTC y la correría un día hacia atrás.
    const ymd = fieldType === 'date' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(text) : null;
    return ymd ? `${ymd[3]}/${ymd[2]}/${ymd[1]}` : text;
  }

  /** Respaldo si el campo ya no existe: «tipo_empresa» → «Tipo empresa», nunca la llave cruda. */
  private humanizeKey(key: string): string {
    const text = key.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Campo';
  }

  private resetSubResources(): void {
    this.participants = [];
    this.agendaItems = [];
    this.reminders = [];
    this.rescheduleLogs = [];
    this.auditLogs = [];
    this.timelineLogs = [];
    this.loadedParticipants = false;
    this.loadedAgenda = false;
    this.loadedReminders = false;
    this.loadedLogs = false;
    this.cancelAgendaForm();
    this.cancelReminderForm();
  }

  setTab(tab: TabKey): void {
    this.activeTab = tab;
    if (!this.meeting?.id) return;

    if (tab === 'participants' && !this.loadedParticipants) this.loadParticipants();
    if (tab === 'agenda' && !this.loadedAgenda) this.loadAgenda();
    if (tab === 'reminders' && !this.loadedReminders) this.loadReminders();
    if (tab === 'logs' && !this.loadedLogs) this.loadLogs();
  }

  private loadParticipants(): void {
    if (!this.meeting?.id) return;
    this.loadingParticipants = true;
    this.meetingApi.listParticipants(this.meeting.id)
      .pipe(catchError(() => of([] as MeetingParticipantResponse[])))
      .subscribe((data) => {
        this.participants = data;
        this.loadingParticipants = false;
        this.loadedParticipants = true;
      });
  }

  private loadAgenda(): void {
    if (!this.meeting?.id) return;
    this.loadingAgenda = true;
    this.meetingApi.listAgenda(this.meeting.id)
      .pipe(catchError(() => of([] as MeetingAgendaItemResponse[])))
      .subscribe((data) => {
        this.agendaItems = [...data].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
        this.loadingAgenda = false;
        this.loadedAgenda = true;
      });
  }

  private loadReminders(): void {
    if (!this.meeting?.id) return;
    // INC 72 — plantillas para resolver el nombre en la lista y poblar el selector.
    this.loadReminderTemplates();
    this.loadingReminders = true;
    this.meetingApi.listReminders(this.meeting.id)
      .pipe(catchError(() => of([] as MeetingReminderResponse[])))
      .subscribe((data) => {
        this.reminders = [...data].sort(
          (a, b) => (a.reminderMinutesBefore ?? 0) - (b.reminderMinutesBefore ?? 0)
        );
        this.loadingReminders = false;
        this.loadedReminders = true;
      });
  }

  private loadLogs(): void {
    if (!this.meeting?.id) return;
    this.loadingLogs = true;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    forkJoin({
      reschedules: this.meetingApi.listRescheduleLogs(this.meeting.id, 0, 50)
        .pipe(catchError(() => of({ content: [] as MeetingRescheduleLogResponse[] } as any))),
      audits: this.meetingApi.listAuditLogs(this.meeting.id, businessId)
        .pipe(catchError(() => of([] as MeetingAuditLogResponse[]))),
    }).subscribe(({ reschedules, audits }) => {
      this.rescheduleLogs = reschedules.content || [];
      this.auditLogs = [...audits].sort((a, b) =>
        (b.datetimeCreated || '').localeCompare(a.datetimeCreated || '')
      );
      this.timelineLogs = this.auditLogs.map((l) => this.toAuditLogResponse(l));
      this.loadingLogs = false;
      this.loadedLogs = true;
    });
  }

  /**
   * INC 47 — adapta el shape del audit-log de reuniones al `AuditLogResponse`
   * estándar que consume el componente compartido <app-audit-timeline> (el mismo
   * de Tickets/Negocios). Pinta fecha, usuario, acción (actionLabel) y detalle
   * (changesDescription con fallback a changes).
   */
  private toAuditLogResponse(l: MeetingAuditLogResponse): AuditLogResponse {
    return {
      id: l.id,
      entityName: l.entityType || 'meeting',
      idEntity: String(l.entityId ?? ''),
      moduleName: 'meeting',
      operationType: this.mapAuditOperationType(l.action),
      operationTypeLabel: l.actionLabel ?? null,
      // INC 47 — diff campo-por-campo (valores ya legibles del backend). `[]` en filas
      // "created"/legadas → hasDiff=false y se usa `description` (changesDescription).
      // `undefined` → null (sin diff enriquecido).
      changesDisplay: l.fieldChanges
        ? l.fieldChanges.map((fc) => ({
            field: fc.field,
            label: fc.label,
            oldValue: fc.oldValue,
            newValue: fc.newValue,
          }))
        : null,
      idUser: l.userId ?? 0,
      username: '',
      userFullName: l.userFullName,
      businessId: l.businessId,
      timestamp: l.datetimeCreated ?? '',
      ipAddress: l.ipAddress,
      description: l.changesDescription ?? l.changes ?? '',
      severity: 'INFO',
      success: true,
    };
  }

  /** Normaliza la acción del audit-log a los tipos que colorea el timeline estándar. */
  private mapAuditOperationType(action?: string): string {
    switch ((action || '').toLowerCase()) {
      case 'create':
      case 'created': return 'CREATE';
      case 'update':
      case 'updated':
      case 'status_change': return 'UPDATE';
      case 'delete':
      case 'deleted': return 'DELETE';
      default: return (action || '').toUpperCase();
    }
  }

  private cargarCatalogos(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    if (this.meeting?.idMeetingType) {
      this.meetingTypeApi.listActive(businessId).subscribe({
        next: (types) => (this.meetingTypes = types),
      });
    }

    // Siempre cargar estados: el historial los usa para etiquetar transiciones
    this.meetingStatusApi.listActive(businessId).subscribe({
      next: (statuses) => (this.meetingStatuses = statuses),
    });
  }

  private cargarOrganizador(): void {
    if (!this.meeting?.organizerId) {
      this.organizerName = 'Sin organizador';
      return;
    }

    const creatorId = this.meeting.organizerId;
    const currentUser = this.authService.getCurrentUser();
    const currentUserId = currentUser?.id;

    if (creatorId === currentUserId && currentUser) {
      this.organizerName = `${currentUser.fullname || ''} ${currentUser.lastname || ''}`.trim();
      return;
    }

    this.authApiService.getUserById(creatorId).subscribe({
      next: (user) => {
        this.organizerName = `${user.fullname || ''} ${user.lastname || ''}`.trim();
      },
      error: () => {
        // El directorio resuelve también a los inactivos: fallar aquí es «ya no existe».
        this.organizerName = 'Usuario no disponible';
      },
    });
  }

  private cargarNombresRelaciones(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    if (this.meeting?.contactId) {
      this.contactoApi
        .searchContacts({ businessId, searchTerm: '', page: 0, size: 100 })
        .pipe(catchError(() => of({ content: [] })))
        .subscribe((response: any) => {
          const contact = response.content.find(
            (c: any) => c.id === this.meeting?.contactId
          );
          this.contactName = contact
            ? `${contact.fullname || ''}${contact.lastname ? ' ' + contact.lastname : ''}`.trim()
            : 'Contacto no encontrado';
        });
    }

    if (this.meeting?.companyId) {
      this.empresaApi
        .searchCompanies({ businessId, searchTerm: '', page: 0, size: 100 })
        .pipe(catchError(() => of({ content: [] })))
        .subscribe((response: any) => {
          const company = response.content.find(
            (c: any) => c.id === this.meeting?.companyId
          );
          this.companyName = company
            ? company.commercialName || company.businessName || ''
            : 'Empresa no encontrada';
        });
    }

    if (this.meeting?.dealId) {
      this.dealApi
        .searchDeals({ businessId, searchName: '', page: 0, size: 100 })
        .pipe(catchError(() => of({ content: [] })))
        .subscribe((response: any) => {
          const deal = response.content.find(
            (d: any) => d.id === this.meeting?.dealId
          );
          this.dealName = deal ? deal.name || deal.title || '' : 'Negocio no encontrado';
        });
    }
  }

  onClose(): void {
    this.close.emit();
  }

  /**
   * INC 83 — la notificación "Nueva reunión agendada" ahora abre este detalle en vez
   * del formulario, así que el detalle tiene que ofrecer la salida a Editar (sujeta a
   * `meetings:edit`, el mismo permiso que el botón de la lista).
   */
  editarReunion(): void {
    if (!this.meeting?.id) return;
    this.close.emit();
    this.router.navigate(['/meeting/editar', this.meeting.id]);
  }

  // ─── INC 45: Agenda CRUD ───────────────────────────────────────

  private emptyAgendaModel(): AgendaFormModel {
    return { title: '', description: '', durationMinutes: null, presenter: '', order: null };
  }

  openNewAgenda(): void {
    this.editingAgendaId = null;
    this.agendaModel = this.emptyAgendaModel();
    const maxOrder = this.agendaItems.reduce((m, i) => Math.max(m, i.order ?? 0), 0);
    this.agendaModel.order = maxOrder + 1;
    this.showAgendaForm = true;
  }

  openEditAgenda(item: MeetingAgendaItemResponse): void {
    this.editingAgendaId = item.id;
    this.agendaModel = {
      title: item.title || '',
      description: item.description || '',
      durationMinutes: item.durationMinutes ?? null,
      presenter: item.presenter || '',
      order: item.order ?? null,
    };
    this.showAgendaForm = true;
  }

  cancelAgendaForm(): void {
    this.showAgendaForm = false;
    this.editingAgendaId = null;
    this.savingAgenda = false;
    this.agendaModel = this.emptyAgendaModel();
  }

  saveAgenda(): void {
    if (!this.meeting?.id) return;
    const title = this.agendaModel.title.trim();
    if (!title) {
      this.toast.error({ title: 'Error', message: 'El título del item es obligatorio.' });
      return;
    }

    const meetingId = this.meeting.id;
    const body: MeetingAgendaItemRequest = {
      idMeeting: meetingId,
      title,
    };
    if (this.agendaModel.description.trim()) body.description = this.agendaModel.description.trim();
    if (this.agendaModel.durationMinutes != null) body.durationMinutes = this.agendaModel.durationMinutes;
    if (this.agendaModel.presenter.trim()) body.presenter = this.agendaModel.presenter.trim();
    if (this.agendaModel.order != null) body.order = this.agendaModel.order;

    this.savingAgenda = true;
    const editingId = this.editingAgendaId;
    const done = () => {
      this.savingAgenda = false;
      this.cancelAgendaForm();
      this.loadAgenda();
    };

    if (editingId != null) {
      this.meetingApi.updateAgendaItem(meetingId, editingId, body).subscribe({
        next: () => { this.toast.updated('Item de agenda'); done(); },
        error: (err) => {
          this.savingAgenda = false;
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al actualizar el item de agenda' });
        },
      });
    } else {
      this.meetingApi.createAgendaItem(meetingId, body).subscribe({
        next: () => { this.toast.created('Item de agenda'); done(); },
        error: (err) => {
          this.savingAgenda = false;
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear el item de agenda' });
        },
      });
    }
  }

  async deleteAgenda(item: MeetingAgendaItemResponse): Promise<void> {
    if (!this.meeting?.id) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar item de agenda',
      message: `¿Eliminar "${item.title}" de la agenda?`,
    });
    if (!ok) return;

    this.meetingApi.deleteAgendaItem(this.meeting.id, item.id).subscribe({
      next: () => { this.toast.deleted('Item de agenda'); this.loadAgenda(); },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el item de agenda' });
      },
    });
  }

  // ─── INC 46: Recordatorios CRUD ────────────────────────────────

  private emptyReminderModel(): ReminderFormModel {
    return { reminderMinutesBefore: 60, reminderMethod: 'email', reminderTarget: 'organizer', templateId: null };
  }

  /**
   * INC 72 — carga (una vez) las plantillas de correo del negocio para el selector
   * del recordatorio (GET /api/meeting-email-templates?businessId=&page=0&size=50).
   * Se cargan todas las plantillas (no se filtra por tipo) para no ocultar plantillas
   * existentes; si no hay ninguna, la UI muestra el aviso "no hay plantillas".
   */
  private loadReminderTemplates(): void {
    if (this.loadedReminderTemplates) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.reminderTemplatesError = false;
    this.emailTemplateApi
      .list(businessId, 0, 50)
      .subscribe({
        next: (page) => {
          this.reminderTemplates = page?.content || [];
          this.loadedReminderTemplates = true;
        },
        // Antes cualquier fallo (403, 500, sesión caída) caía en un catchError que
        // devolvía una lista vacía, así que la UI decía "no hay plantillas" aunque
        // sí existieran. Un error y una lista vacía deben verse distinto: si no, un
        // fallo de carga es indistinguible de "no hay ninguna" y no se puede
        // diagnosticar. No se marca loadedReminderTemplates para poder reintentar.
        error: () => {
          this.reminderTemplates = [];
          this.reminderTemplatesError = true;
        },
      });
  }

  /** Reintento manual tras un fallo de carga de plantillas. */
  reintentarPlantillas(): void {
    this.loadedReminderTemplates = false;
    this.loadReminderTemplates();
  }

  /** INC 72 — el selector de plantilla solo aplica cuando el método incluye correo. */
  get reminderUsesEmail(): boolean {
    return this.reminderModel.reminderMethod === 'email' || this.reminderModel.reminderMethod === 'all';
  }

  openNewReminder(): void {
    this.editingReminderId = null;
    this.reminderModel = this.emptyReminderModel();
    this.loadReminderTemplates();
    this.showReminderForm = true;
  }

  /**
   * Lleva al usuario a crear plantillas cuando el selector está vacío. El aviso
   * "no hay plantillas" era ambiguo: no distinguía entre "no existen" y "las creé
   * en el módulo equivocado" (las de #/templates son de marketing y no alimentan
   * este selector). Cierra el modal antes de navegar, como hace verLead().
   */
  irAConfigPlantillas(): void {
    this.close.emit();
    this.router.navigate(['/meeting/config/email-templates']);
  }

  openEditReminder(r: MeetingReminderResponse): void {
    this.editingReminderId = r.id;
    this.reminderModel = {
      reminderMinutesBefore: r.reminderMinutesBefore ?? null,
      reminderMethod: (r.reminderMethod as ReminderMethod) || 'email',
      reminderTarget: (r.reminderTarget as ReminderTarget) || 'organizer',
      templateId: r.templateId ?? null,
    };
    this.loadReminderTemplates();
    this.showReminderForm = true;
  }

  cancelReminderForm(): void {
    this.showReminderForm = false;
    this.editingReminderId = null;
    this.savingReminder = false;
    this.reminderModel = this.emptyReminderModel();
  }

  setReminderMinutes(value: number): void {
    this.reminderModel.reminderMinutesBefore = value;
  }

  saveReminder(): void {
    if (!this.meeting?.id) return;
    const minutes = this.reminderModel.reminderMinutesBefore;
    if (minutes == null || minutes <= 0) {
      this.toast.error({ title: 'Error', message: 'La antelación debe ser mayor a 0 minutos.' });
      return;
    }

    const meetingId = this.meeting.id;
    const body: MeetingReminderRequest = {
      idMeeting: meetingId,
      reminderMinutesBefore: minutes,
      reminderMethod: this.reminderModel.reminderMethod,
      reminderTarget: this.reminderModel.reminderTarget,
      // INC 72 — solo enviar templateId si el método incluye correo; null = por defecto.
      templateId: this.reminderUsesEmail ? (this.reminderModel.templateId ?? null) : null,
    };

    this.savingReminder = true;
    const editingId = this.editingReminderId;
    const done = () => {
      this.savingReminder = false;
      this.cancelReminderForm();
      this.loadReminders();
    };

    if (editingId != null) {
      this.meetingApi.updateReminder(meetingId, editingId, body).subscribe({
        next: () => { this.toast.updated('Recordatorio'); done(); },
        error: (err) => {
          this.savingReminder = false;
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al actualizar el recordatorio' });
        },
      });
    } else {
      this.meetingApi.createReminder(meetingId, body).subscribe({
        next: () => { this.toast.created('Recordatorio'); done(); },
        error: (err) => {
          this.savingReminder = false;
          this.toast.error({ title: 'Error', message: err.error?.message || 'Error al crear el recordatorio' });
        },
      });
    }
  }

  async deleteReminder(r: MeetingReminderResponse): Promise<void> {
    if (!this.meeting?.id) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar recordatorio',
      message: `¿Eliminar el recordatorio de ${this.formatReminderMinutes(r.reminderMinutesBefore)}?`,
    });
    if (!ok) return;

    this.meetingApi.deleteReminder(this.meeting.id, r.id).subscribe({
      next: () => { this.toast.deleted('Recordatorio'); this.loadReminders(); },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el recordatorio' });
      },
    });
  }

  // ─── INC 73: estado real del recordatorio ──────────────────────

  /** Estado real; si el backend no lo envía, derivarlo de reminderSent (legacy). */
  getReminderStatus(r: MeetingReminderResponse): ReminderStatus {
    if (r.status) return r.status;
    return r.reminderSent ? 'SENT' : 'PENDING';
  }

  getReminderStatusLabel(r: MeetingReminderResponse): string {
    switch (this.getReminderStatus(r)) {
      case 'SENT': return 'Enviado';
      case 'FAILED': return 'Fallido';
      default: return 'Pendiente';
    }
  }

  getReminderStatusColor(r: MeetingReminderResponse): BadgeColor {
    switch (this.getReminderStatus(r)) {
      case 'SENT': return 'success';
      case 'FAILED': return 'error';
      default: return 'light';
    }
  }

  /** INC 72 — nombre legible de la plantilla asignada a un recordatorio (o ''). */
  getReminderTemplateName(r: MeetingReminderResponse): string {
    if (r.templateId == null) return '';
    const tpl = this.reminderTemplates.find((t) => t.id === r.templateId);
    return tpl?.name || `Plantilla #${r.templateId}`;
  }

  /** "1 día antes", "1 hora antes", "30 min antes". */
  formatReminderMinutes(min?: number): string {
    if (min == null) return '—';
    if (min % 1440 === 0) { const d = min / 1440; return `${d} día${d > 1 ? 's' : ''} antes`; }
    if (min % 60 === 0) { const h = min / 60; return `${h} hora${h > 1 ? 's' : ''} antes`; }
    return `${min} min antes`;
  }

  // ─── Videollamada Jitsi (organizador) ──────────────────────────

  /** True si la reunión usa Jitsi y tiene sala disponible. */
  canJoinJitsi(): boolean {
    return (
      this.meeting?.videoProvider === 'jitsi' &&
      !!(this.meeting?.videoRoomToken || this.meeting?.videoJoinUrl)
    );
  }

  /** roomName de Jitsi: "partnercrm-"+token (igual que la página pública). */
  jitsiRoomName(): string {
    const token = this.meeting?.videoRoomToken;
    return token ? `partnercrm-${token}` : '';
  }

  jitsiDisplayName(): string {
    const u = this.authService.getCurrentUser();
    return `${u?.fullname || ''} ${u?.lastname || ''}`.trim() || 'Organizador';
  }

  jitsiEmail(): string {
    return this.authService.getCurrentUser()?.email || '';
  }

  joinJitsi(): void {
    if (this.canJoinJitsi()) this.jitsiOpen = true;
  }

  closeJitsi(): void {
    this.jitsiOpen = false;
  }

  // ─── Helpers de badges / etiquetas ─────────────────────────────

  getStatusColor(): BadgeColor {
    if (this.estaCancelada) return 'error'; // INC 80
    if (!this.meeting?.idMeetingStatus) return 'light';
    const statusId = this.meeting.idMeetingStatus;
    if (statusId === 1) return 'success';
    if (statusId === 2) return 'warning';
    if (statusId === 3) return 'error';
    return 'info';
  }

  /**
   * INC 80 — cancelada según el criterio único (motivo informado o estado "cancel…").
   * El estado de cancelación es opcional en el catálogo del negocio, así que sin esto
   * una reunión cancelada seguía mostrándose como "Confirmado".
   */
  get estaCancelada(): boolean {
    return isMeetingCancelled(this.meeting, this.meetingStatuses);
  }

  getStatusLabel(): string {
    if (this.estaCancelada) return 'Cancelada';
    if (!this.meeting?.idMeetingStatus) return 'Sin estado';
    const status = this.meetingStatuses.find(
      (s) => s.id === this.meeting?.idMeetingStatus
    );
    return status?.label || status?.name || 'Sin estado';
  }

  getTypeColor(): BadgeColor {
    if (!this.meeting?.idMeetingType) return 'light';
    return 'primary';
  }

  getTypeLabel(): string {
    if (!this.meeting?.idMeetingType) return 'Sin tipo';
    const type = this.meetingTypes.find(
      (t) => t.id === this.meeting?.idMeetingType
    );
    return type?.label || type?.name || 'Sin tipo';
  }

  paymentStatusLabel(status?: string | null): string {
    return meetingLabel(MEETING_PAYMENT_STATUS_LABELS, status);
  }

  sourceLabel(source?: string | null): string {
    return meetingLabel(MEETING_SOURCE_LABELS, source);
  }

  participantStatusLabel(status?: string | null): string {
    return meetingLabel(PARTICIPANT_STATUS_LABELS, status);
  }

  videoProviderLabel(provider?: string | null): string {
    return meetingLabel(VIDEO_PROVIDER_LABELS, provider?.toLowerCase());
  }

  getPaymentColor(status?: string): BadgeColor {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'refunded': return 'info';
      case 'failed': return 'error';
      default: return 'light';
    }
  }

  getParticipantStatusColor(status?: string): BadgeColor {
    switch (status) {
      case 'accepted':
      case 'attended': return 'success';
      case 'declined': return 'error';
      case 'tentative': return 'warning';
      case 'invited': return 'info';
      default: return 'light';
    }
  }

  getReminderMethodLabel(method?: string): string {
    switch (method) {
      case 'email': return 'Correo';
      case 'inapp': return 'Notificación in-app';
      case 'all': return 'Ambos';
      case 'sms': return 'SMS';
      case 'push': return 'Push';
      default: return method || '—';
    }
  }

  getReminderTargetLabel(target?: string): string {
    switch (target) {
      case 'organizer': return 'Organizador';
      case 'guests': return 'Invitados';
      case 'all': return 'Todos';
      default: return target || '—';
    }
  }

  getLogActionLabel(action?: string): string {
    switch (action) {
      case 'reschedule': return 'Reprogramado';
      case 'cancel': return 'Cancelado';
      case 'no_show': return 'No-show';
      default: return action || '—';
    }
  }

  getLogActionColor(action?: string): BadgeColor {
    switch (action) {
      case 'reschedule': return 'warning';
      case 'cancel': return 'error';
      case 'no_show': return 'dark';
      default: return 'light';
    }
  }

  // ─── INC 44: Participantes (nombre legible + rol) ──────────────

  /** Nombre legible: name → email → 'Participante'. Nunca "Usuario #N" ni ids crudos. */
  getParticipantDisplayName(p: MeetingParticipantResponse): string {
    return p.name?.trim() || p.email?.trim() || 'Participante';
  }

  /** Rol legible (en español) ya denormalizado por el backend; fallback por participantType. */
  getParticipantRoleLabel(p: MeetingParticipantResponse): string {
    if (p.role?.trim()) return p.role.trim();
    switch (p.participantType) {
      case 'organizer': return 'Organizador';
      case 'required': return 'Invitado interno';
      case 'optional': return 'Contacto externo';
      default: return 'Participante';
    }
  }

  getParticipantRoleColor(p: MeetingParticipantResponse): BadgeColor {
    const role = this.getParticipantRoleLabel(p).toLowerCase();
    if (role.includes('organizador')) return 'primary';
    if (role.includes('interno')) return 'info';
    if (role.includes('externo') || role.includes('contacto')) return 'warning';
    return 'light';
  }

  // Relaciones
  getContactName(): string { return this.contactName || 'Sin contacto'; }
  getCompanyName(): string { return this.companyName || 'Sin empresa'; }
  getDealName(): string { return this.dealName || 'Sin negocio'; }
  getOrganizerName(): string { return this.organizerName || 'Sin organizador'; }

  // Format
  formatFechaCompleta(dateString?: string): string {
    if (!dateString) return '—';
    try {
      return new Intl.DateTimeFormat('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(dateString));
    } catch {
      return '—';
    }
  }

  formatFecha(dateString?: string): string {
    if (!dateString) return '—';
    try {
      return new Intl.DateTimeFormat('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(new Date(dateString));
    } catch {
      return '—';
    }
  }
}
